import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import {
  AppBar,
  CardActions,
  CardContent,
  Dialog,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  styled,
  Toolbar,
  Typography,
} from "@mui/material";
import PrimaryButton from "components/PrimaryButton";
import StyledCard from "components/StyledCard";
import { StyledContainer } from "components/StyledContainer";
import { addressToString } from "helpers";
import { MachineType } from "operations/schema/schema";
import { FC, useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

const StyledDetails = styled("div")(() => ({
  marginTop: 5,
  marginLeft: 14,
}));

interface ScannerMachineSuggestDialogProps {
  open: boolean;
  scannerValue: string | null;
  machines: MachineType[];
  selectMachine: (selectedMachine: MachineType) => void;
  onClose: () => void;
}

export const ScannerMachineSuggestDialog: FC<ScannerMachineSuggestDialogProps> = (props) => {
  const { open, scannerValue, machines, selectMachine, onClose } = props;
  const intl = useIntl();
  const [selectedMachine, setSelectedMachine] = useState<MachineType | null>(null);

  const handleClose = useCallback(() => {
    setSelectedMachine(null);
    onClose();
  }, [onClose]);

  const onSelect = useCallback(() => {
    if (selectedMachine) selectMachine(selectedMachine);
    handleClose();
  }, [handleClose, selectMachine, selectedMachine]);

  return (
    <Dialog fullScreen open={open} onClose={handleClose} data-testid="ScannerMachineSuggestDialog">
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="ScannerMachineSuggestDialog-CloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" marginLeft="16px" flex={1}>
            <FormattedMessage id="barcode.suggestedMatches" />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <StyledContainer sx={{ mt: 1 }}>
        <Grid container alignItems="center" minWidth="100%" mt={2}>
          {intl.formatMessage({ id: "barcode.noMatchSuggested" }, { scannedValue: scannerValue })}
        </Grid>
        <Grid container alignItems="center" minWidth="100%" mt={2}>
          <RadioGroup row value={selectedMachine} name="selectedMachine" sx={{ minWidth: "100%" }}>
            {machines.map((machine) => (
              <Grid item alignItems="center" key={machine.machineNo} xs={12}>
                <StyledCard onClick={() => setSelectedMachine(machine)}>
                  <CardContent sx={{ width: "100%" }}>
                    <StyledDetails data-testid="ChangeJobMachine-SelectedMachine">
                      <Typography variant="body1" component="h3">
                        {machine.machineNo}
                      </Typography>
                      {!!machine.location && (
                        <Typography variant="body2" color="textSecondary">
                          {machine.location}
                        </Typography>
                      )}
                      {!!machine.name && (
                        <Typography variant="body2" color="textSecondary">
                          {machine.name}
                        </Typography>
                      )}
                      {!!machine.idNo && (
                        <Typography variant="body2" color="textSecondary">
                          {machine.idNo}
                        </Typography>
                      )}
                      {!!machine.address && (
                        <Typography variant="body2" color="textSecondary">
                          {addressToString(machine.address)}
                        </Typography>
                      )}
                    </StyledDetails>
                  </CardContent>
                  <CardActions>
                    <Radio value={machine} onClick={() => setSelectedMachine(machine)} />
                  </CardActions>
                </StyledCard>
              </Grid>
            ))}
          </RadioGroup>
        </Grid>
        <Grid item>
          <PrimaryButton
            fullWidth
            variant="contained"
            onClick={onSelect}
            disabled={!selectedMachine}
            data-testid="ScannerMachineSuggestDialog-AddPart"
          >
            {intl.formatMessage({
              id: "barcode.select",
            })}
          </PrimaryButton>
        </Grid>
      </StyledContainer>
    </Dialog>
  );
};
